import {
  IBuilding,
  IBuildingEstimatedRent,
  IBuildingGrossYield,
  IBuildingOtherTypeUnit,
  IPartialProperty,
} from '../types/types';

export interface IBuildingListProperty {
  id: number;
  dataset: string;
  name?: string | null;
  address?: string | null;
  city?: string | null;
  postalCode?: string | null;
  countryCode?: string | null;
  numberOfRooms?: number | string | null;
  numberOfSquareMeters?: number | null;
  currentRent?: number | null;
  estimatedRent?: number | null;
  rentDifferencePercent?: number | null;
  isEstimatedRentPending?: boolean;
  reportStatus?: string;
  geometry?: any;
}

export interface IBuildingGroup {
  buildingKey: string;
  buildingId: number;
  dataset: string;
  buildingName?: string | null;
  yearBuilt?: number | null;
  buildingType?: string | null;
  isProcessing: boolean;
  pendingUnitsCount: number;
  includedUnitsCount: number;
  excludedUnitsCount: number;
  indicativeValue: number | null;
  valuedUnitsCount: number;
  grossYield: IBuildingGrossYield | null;
  grossYieldExcludingOwnCompany: IBuildingGrossYield | null;
  isValuationPartial: boolean;
  pricePendingUnitsCount: number;
  priceNoComparablesUnitsCount: number;
  otherDwellingTypeUnits: IBuildingOtherTypeUnit[];
  isValuationSupported: boolean;
  address: string;
  city?: string | null;
  postalCode?: string | null;
  countryCode?: string | null;
  properties: IBuildingListProperty[];
  unitsCount: number;
  totalArea: number;
  totalAnnualRent: number;
}

// The property list is unordered, so `properties[0]` isn't a stable unit.
export const getPrimaryBuildingProperty = (
  properties: IBuildingListProperty[],
): IBuildingListProperty | undefined =>
  properties?.length
    ? [...properties].sort((a, b) => a.id - b.id)[0]
    : undefined;

export const toBuildingListProperty =
  (excludeOwnCompany: boolean) =>
  (property: IPartialProperty): IBuildingListProperty => ({
    id: property.id,
    dataset: property.dataset,
    name: property.name,
    address: property.address,
    city: property.municipality,
    postalCode: property.areaCode,
    countryCode: property.countryCode,
    numberOfRooms: property.numberOfRooms,
    numberOfSquareMeters: property.numberOfSquareMeters,
    currentRent: property.rentPerMonth,
    estimatedRent: excludeOwnCompany
      ? property.rentWithExcludedOwnCompany
      : property.estimatedRent,
    rentDifferencePercent: excludeOwnCompany
      ? (property.estimatedRentDifferencePercentageExcludedOwnCompanies ?? null)
      : (property.estimatedRentDifferencePercentage ?? null),
    reportStatus: property.reportStatus,
    geometry: property.geometry,
  });

export const withEstimatedRents = (
  properties: IBuildingListProperty[],
  rentsByPropertyId: Map<number, IBuildingEstimatedRent>,
  excludeOwnCompany: boolean,
  ignorePending = false,
): IBuildingListProperty[] =>
  rentsByPropertyId.size
    ? properties.map((property) => {
        const rent = rentsByPropertyId.get(property.id);
        if (!rent) return property;
        return {
          ...property,
          estimatedRent: excludeOwnCompany
            ? rent.rentWithExcludedOwnCompany
            : rent.estimatedRent,
          rentDifferencePercent: excludeOwnCompany
            ? rent.estimatedRentDifferencePercentageExcludedOwnCompanies
            : rent.estimatedRentDifferencePercentage,
          isEstimatedRentPending: rent.isRentPending && !ignorePending,
        };
      })
    : properties;

export const getBuildingFullAddress = (building: IBuildingGroup) =>
  [
    building.address,
    [building.postalCode, building.city].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ');

type IBuildingRef = Pick<IBuilding, 'dataset' | 'buildingId'>;

export const getBuildingKey = ({ dataset, buildingId }: IBuildingRef): string =>
  `${dataset}:${buildingId}`;

export const getBuildingPath = (
  { dataset, buildingId }: IBuildingRef,
  view: 'details' | 'edit' = 'details',
) =>
  `/optimize/rent-navigator/properties/buildings/${encodeURIComponent(dataset)}/${buildingId}/${view}`;

export const getBuildingDetailsPath = (building: IBuildingRef) =>
  getBuildingPath(building, 'details');

export const getBuildingKeyFromParams = ({
  dataset,
  buildingId,
}: {
  dataset?: string;
  buildingId?: string;
}): string | null =>
  dataset && buildingId && /^\d+$/.test(buildingId)
    ? getBuildingKey({ dataset, buildingId: Number(buildingId) })
    : null;

export const findPropertyBuilding = (
  buildings: IBuilding[],
  property: { id: number; dataset: string },
): IBuilding | undefined =>
  buildings?.find(
    (building) =>
      building.dataset === property.dataset &&
      building.propertyIds.includes(property.id),
  );

const toBuildingGroup = (building: IBuilding): IBuildingGroup => ({
  buildingKey: getBuildingKey(building),
  buildingId: building.buildingId,
  dataset: building.dataset,
  buildingName: building.buildingName,
  yearBuilt: building.yearBuilt,
  buildingType: building.buildingType,
  isProcessing: building.isProcessing,
  pendingUnitsCount: building.pendingUnitsCount,
  includedUnitsCount: building.includedUnitsCount,
  excludedUnitsCount: building.excludedUnitsCount,
  indicativeValue: building.indicativeValue,
  valuedUnitsCount: building.valuedUnitsCount,
  grossYield: building.grossYield,
  grossYieldExcludingOwnCompany: building.grossYieldExcludingOwnCompany,
  isValuationPartial: building.isValuationPartial,
  pricePendingUnitsCount: building.pricePendingUnitsCount ?? 0,
  priceNoComparablesUnitsCount: building.priceNoComparablesUnitsCount ?? 0,
  otherDwellingTypeUnits: building.otherDwellingTypeUnits ?? [],
  isValuationSupported: building.isValuationSupported,
  // buildingAddress is "<street> <number>, <municipality>"
  address: building.buildingAddress.split(',')[0].trim(),
  city: building.municipality,
  postalCode: building.areaCode,
  countryCode: building.countryCode,
  properties: [],
  unitsCount: building.unitsCount,
  totalArea: building.totalArea,
  totalAnnualRent: building.totalAnnualRent,
});

export const groupPropertiesByBuildings = <T extends IBuildingListProperty>(
  buildings: IBuilding[],
  units: T[],
): IBuildingGroup[] => {
  const buildingByUnit = new Map<string, IBuilding>();
  buildings?.forEach((building) =>
    building.propertyIds.forEach((propertyId) =>
      buildingByUnit.set(`${building.dataset}:${propertyId}`, building),
    ),
  );

  const groups = new Map<string, IBuildingGroup>();
  units?.forEach((unit) => {
    const building = buildingByUnit.get(`${unit.dataset}:${unit.id}`);
    if (!building) return;

    const key = getBuildingKey(building);
    const group = groups.get(key) ?? toBuildingGroup(building);
    group.properties.push(unit);
    groups.set(key, group);
  });

  return Array.from(groups.values());
};

export const MAX_BUILDING_NAME_LENGTH = 80;

export const getBuildingDisplayName = (
  building: Pick<IBuildingGroup, 'buildingName' | 'address'>,
) => building.buildingName || building.address;

// Rent difference of the whole building: its units' total contract rent
// against their total estimated rent, over units that have both.
export const getBuildingMarketDiffPercent = (
  properties: Pick<IBuildingListProperty, 'currentRent' | 'estimatedRent'>[],
): number | null => {
  const priced = properties.filter(
    (unit) => (unit.currentRent || 0) > 0 && (unit.estimatedRent || 0) > 0,
  );
  const totalEstimated = priced.reduce(
    (sum, unit) => sum + (unit.estimatedRent || 0),
    0,
  );
  if (!totalEstimated) return null;
  const totalCurrent = priced.reduce(
    (sum, unit) => sum + (unit.currentRent || 0),
    0,
  );
  return ((totalCurrent - totalEstimated) / totalEstimated) * 100;
};

export const getBuildingKeyFromLocationPath = (
  pathname: string,
): string | null => {
  const match = pathname.match(/\/buildings\/([^/]+)\/(\d+)(?:\/|$)/);
  return match
    ? getBuildingKeyFromParams({
        dataset: decodeURIComponent(match[1]),
        buildingId: match[2],
      })
    : null;
};

export const isSameCountryBuilding = (
  building: Pick<IBuildingGroup, 'countryCode'>,
  countryCode?: string | null,
) =>
  !countryCode ||
  !building.countryCode ||
  building.countryCode.toLowerCase() === countryCode.toLowerCase();

export const getBuildingEstimatedAnnualRent = (
  properties: Pick<IBuildingListProperty, 'estimatedRent'>[],
): number | null => {
  const total = properties.reduce(
    (sum, unit) => sum + (unit.estimatedRent || 0),
    0,
  );
  return total > 0 ? total * 12 : null;
};

export type UnitTypeBucket = 'studio' | '1' | '2' | '3' | '4' | '5+';

const UNIT_TYPE_BUCKET_ORDER: UnitTypeBucket[] = [
  'studio',
  '1',
  '2',
  '3',
  '4',
  '5+',
];

export const getUnitTypeBucket = (
  numberOfRooms: number | string | null | undefined,
): UnitTypeBucket | null => {
  if (numberOfRooms == null) return null;

  const rooms =
    typeof numberOfRooms === 'string'
      ? parseInt(numberOfRooms, 10)
      : numberOfRooms;

  if (Number.isNaN(rooms)) return null;
  if (rooms <= 0) return 'studio';
  if (rooms >= 5) return '5+';
  return String(rooms) as UnitTypeBucket;
};

export interface IUnitTypeIncome {
  bucket: UnitTypeBucket;
  monthlyIncome: number;
  unitsCount: number;
}

export const getIncomeByUnitType = (
  properties: IBuildingListProperty[],
): IUnitTypeIncome[] => {
  const buckets = new Map<UnitTypeBucket, IUnitTypeIncome>();

  properties?.forEach((property) => {
    const bucket = getUnitTypeBucket(property.numberOfRooms);
    if (!bucket || !property.currentRent) return;

    const existing = buckets.get(bucket) || {
      bucket,
      monthlyIncome: 0,
      unitsCount: 0,
    };
    existing.monthlyIncome += property.currentRent || 0;
    existing.unitsCount += 1;
    buckets.set(bucket, existing);
  });

  return UNIT_TYPE_BUCKET_ORDER.filter((bucket) => buckets.has(bucket)).map(
    (bucket) => buckets.get(bucket) as IUnitTypeIncome,
  );
};
